import { useState } from "react";
import { useDeleteFile } from "@/hooks/useFiles";
import { File, Folder } from "@/types/breeze/File";
import ImageGalleryOverlay from "@/components/ui/ImageGalleryOverlay";
import { downloadFile, formatFileSize, formatDimensions, formatDuration, formatBitrate, isPreviewable, resolveMediaCategory } from "@/utils/fileUtils";
import toast from "react-hot-toast";
import PageTemplate from "@/components/page-template/PageTemplate";
import {
  Box, Flex, Text, Button, TextField, Dialog, IconButton,
} from "@radix-ui/themes";
import { useConfirm } from "@/providers/ConfirmDialogProvider";
import { fileService } from "@/services/breeze/FileService";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  FolderIcon, FolderPlusIcon, ChevronRightIcon, HomeIcon,
  UploadIcon, ChevronDownIcon,
} from "lucide-react";
import FileUploadZone from "@/components/files/FileUploadZone";
import SplitView from "@/components/ui/SplitView";
import PaginatedFileGrid from "@/components/files/PaginatedFileGrid";
import { BulkFileActions } from "@/components/files/FileActions";

export default function FilesPage() {
  const { mutate: deleteFile } = useDeleteFile();
  const confirm = useConfirm();
  const queryClient = useQueryClient();

  const [currentFolderId, setCurrentFolderId] = useState<number | null>(null);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [galleryFiles, setGalleryFiles] = useState<File[]>([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [uploadLabel, setUploadLabel] = useState<string | null>(null);
  const [showUploadZone, setShowUploadZone] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);

  // Dialogs
  const [showCreateFolder, setShowCreateFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [renamingFolder, setRenamingFolder] = useState<Folder | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const [editingFile, setEditingFile] = useState<File | null>(null);

  // Data
  const { data: folderContents } = useQuery({
    queryKey: ["folder-contents", currentFolderId],
    queryFn: () => currentFolderId ? fileService.getFolderContents(currentFolderId) : null,
    enabled: currentFolderId !== null,
  });

  const { data: rootFolders = [] } = useQuery({
    queryKey: ["root-folders"],
    queryFn: () => fileService.getFolders(),
    enabled: currentFolderId === null,
  });

  const { data: folderTree = [] } = useQuery({
    queryKey: ["folder-tree"],
    queryFn: () => fileService.getFolderTree(),
  });

  const folders = currentFolderId ? (folderContents?.sub_folders || []) : rootFolders;
  const breadcrumb = folderContents?.breadcrumb || [];

  const navigateToFolder = (folderId: number | null) => {
    setCurrentFolderId(folderId);
    setSelectedFiles([]);
  };

  const invalidateFolders = () => {
    queryClient.invalidateQueries({ queryKey: ["folder-contents"] });
    queryClient.invalidateQueries({ queryKey: ["root-folders"] });
    queryClient.invalidateQueries({ queryKey: ["folder-tree"] });
  };

  // ── Tree ─────────────────────────────────────────────────────────────

  const buildTree = (allFolders: Folder[], parentId: number | null = null): Folder[] =>
    allFolders.filter((f) => f.parent_folder_id === parentId).sort((a, b) => a.name.localeCompare(b.name));

  const [expandedFolders, setExpandedFolders] = useState<Set<number>>(new Set());
  const effectiveExpanded = new Set(expandedFolders);
  breadcrumb.forEach((b) => effectiveExpanded.add(b.id));

  const toggleExpanded = (folderId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedFolders((prev) => {
      const next = new Set(prev);
      if (next.has(folderId)) next.delete(folderId); else next.add(folderId);
      return next;
    });
  };

  const FolderTreeItem = ({ folder, depth = 0, isLast = false, parentLines = [] as boolean[] }: { folder: Folder; depth?: number; isLast?: boolean; parentLines?: boolean[] }) => {
    const children = buildTree(folderTree, folder.id);
    const isActive = currentFolderId === folder.id;
    const isInPath = breadcrumb.some((b) => b.id === folder.id);
    const hasChildren = children.length > 0;
    const isExpanded = effectiveExpanded.has(folder.id);
    const childParentLines = [...parentLines, !isLast];

    return (
      <>
        <Flex align="center" style={{ height: 30 }}>
          {parentLines.map((showLine, i) => (
            <Box key={i} style={{ width: 18, height: "100%", position: "relative", flexShrink: 0 }}>
              {showLine && <Box style={{ position: "absolute", left: 8, top: 0, width: 1, height: "100%", backgroundColor: "var(--gray-6)" }} />}
            </Box>
          ))}
          <Box style={{ width: 18, height: "100%", position: "relative", flexShrink: 0 }}>
            <Box style={{ position: "absolute", left: 8, top: 0, width: 1, height: isLast ? "50%" : "100%", backgroundColor: "var(--gray-6)" }} />
            <Box style={{ position: "absolute", left: 8, top: "50%", width: 10, height: 1, backgroundColor: "var(--gray-6)" }} />
          </Box>
          <Flex
            align="center" py="1" px="1" gap="1"
            style={{ flex: 1, minWidth: 0, cursor: "pointer", backgroundColor: isActive ? "var(--accent-3)" : "transparent", borderRadius: "var(--radius-2)" }}
            onClick={() => navigateToFolder(folder.id)}
          >
            <Box style={{ width: 14, height: 14, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}
              onClick={(e) => hasChildren && toggleExpanded(folder.id, e)}>
              {hasChildren && <ChevronDownIcon size={11} color="var(--gray-8)" style={{ transition: "transform 0.15s", transform: isExpanded ? "rotate(0deg)" : "rotate(-90deg)" }} />}
            </Box>
            <FolderIcon size={14} color="var(--accent-9)" fill={isActive || isInPath ? "var(--accent-4)" : "none"} style={{ flexShrink: 0 }} />
            <Text size="2" weight={isActive ? "medium" : "regular"} truncate style={{ flex: 1 }}>{folder.name}</Text>
          </Flex>
        </Flex>
        {isExpanded && children.map((child, idx) => (
          <FolderTreeItem key={child.id} folder={child} depth={depth + 1} isLast={idx === children.length - 1} parentLines={childParentLines} />
        ))}
      </>
    );
  };

  // ── Operations ───────────────────────────────────────────────────────

  const handleFileUpload = async (newFiles: globalThis.File[]) => {
    if (newFiles.length === 0) return;
    setUploadingFiles(true);
    setUploadProgress(0);
    try {
      for (let i = 0; i < newFiles.length; i++) {
        const file = newFiles[i];
        const formData = new FormData();
        formData.append("file", file);
        setUploadLabel(
          newFiles.length > 1
            ? `Uploading ${file.name} (${i + 1}/${newFiles.length})`
            : `Uploading ${file.name}`
        );
        setUploadProgress(0);
        await fileService.create(
          formData,
          false,
          undefined,
          undefined,
          currentFolderId ?? undefined,
          (percent) => setUploadProgress(percent),
        );
      }
      toast.success(`Uploaded ${newFiles.length} file${newFiles.length > 1 ? "s" : ""}`);
      queryClient.invalidateQueries({ queryKey: ["files"] });
      invalidateFolders();
      // Probe runs asynchronously in AnyWorker.Files; re-fetch a couple of times so the
      // grid picks up the worker's metadata writeback without the user having to refresh.
      setTimeout(() => queryClient.invalidateQueries({ queryKey: ["files"] }), 2000);
      setTimeout(() => queryClient.invalidateQueries({ queryKey: ["files"] }), 6000);
      setShowUploadZone(false);
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploadingFiles(false);
      setUploadProgress(null);
      setUploadLabel(null);
    }
  };

  const handleDeleteFile = async (id: string) => {
    const confirmed = await confirm({ title: "Delete File", message: "Are you sure you want to delete this file?" });
    if (confirmed) {
      deleteFile(id);
      setSelectedFiles((prev) => prev.filter((f) => f.id !== Number(id)));
    }
  };

  const handleOpenGallery = (index: number, files: File[]) => {
    const previewable = files.filter(isPreviewable);
    const selectedFile = files[index];
    const previewIndex = previewable.findIndex((f) => f.id === selectedFile.id);
    setSelectedImageIndex(previewIndex >= 0 ? previewIndex : 0);
    setGalleryFiles(previewable);
    setGalleryOpen(true);
  };

  const handleSaveFileMetadata = async (data: Record<string, string | undefined>) => {
    if (!editingFile) return;
    try {
      await fileService.updateFile(editingFile.id, data);
      queryClient.invalidateQueries({ queryKey: ["files"] });
      setEditingFile(null);
      toast.success("File updated");
    } catch (err: any) {
      toast.error(err?.message || "Failed to update file");
    }
  };

  const handleBulkDelete = async () => {
    if (selectedFiles.length === 0) return;
    const confirmed = await confirm({ title: "Delete Files", message: `Delete ${selectedFiles.length} file(s)?` });
    if (confirmed) {
      selectedFiles.forEach((file) => deleteFile(String(file.id)));
      setSelectedFiles([]);
    }
  };

  const handleBulkDownload = async () => {
    if (selectedFiles.length === 0) return;
    toast.success(`Downloading ${selectedFiles.length} file(s)`);
    for (const file of selectedFiles) {
      try { await downloadFile(file); await new Promise((r) => setTimeout(r, 500)); }
      catch { toast.error(`Failed to download ${file.original_file_name}`); }
    }
  };

  const handleBulkReprobe = async () => {
    if (selectedFiles.length === 0) return;
    const toastId = toast.loading(`Queuing 0/${selectedFiles.length}…`);
    let queued = 0;
    let failed = 0;
    for (let i = 0; i < selectedFiles.length; i++) {
      const file = selectedFiles[i];
      try {
        await fileService.reprobe(file.id);
        queued++;
      } catch {
        failed++;
      }
      toast.loading(`Queuing ${i + 1}/${selectedFiles.length}…`, { id: toastId });
    }
    toast.success(
      `Queued ${queued} file${queued === 1 ? "" : "s"} for reprobe${failed ? ` (${failed} failed)` : ""}. ` +
        `Metadata refreshes when the worker finishes.`,
      { id: toastId },
    );
    // Worker is async — re-fetch a couple of times to pick up new metadata as it lands.
    setTimeout(() => queryClient.invalidateQueries({ queryKey: ["files"] }), 2000);
    setTimeout(() => queryClient.invalidateQueries({ queryKey: ["files"] }), 6000);
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;
    try {
      await fileService.createFolder({ name: newFolderName.trim(), parent_folder_id: currentFolderId });
      setNewFolderName("");
      setShowCreateFolder(false);
      invalidateFolders();
    } catch (err: any) { toast.error(err?.message || "Failed to create folder"); }
  };

  const handleRenameFolder = async () => {
    if (!renamingFolder || !renameValue.trim()) return;
    try {
      await fileService.updateFolder(renamingFolder.id, { name: renameValue.trim() });
      setRenamingFolder(null);
      invalidateFolders();
    } catch (err: any) { toast.error(err?.message || "Failed to rename folder"); }
  };

  const handleDeleteFolder = async (folder: Folder) => {
    const confirmed = await confirm({ title: "Delete Folder", message: `Delete "${folder.name}"? Contents will be moved to the parent folder.` });
    if (confirmed) {
      try {
        await fileService.deleteFolder(folder.id, false);
        if (currentFolderId === folder.id) navigateToFolder(folder.parent_folder_id);
        invalidateFolders();
      } catch (err: any) { toast.error(err?.message || "Failed to delete folder"); }
    }
  };

  // ── Render ───────────────────────────────────────────────────────────

  const rootTreeFolders = buildTree(folderTree, null);

  return (
    <PageTemplate title="File Explorer">
      <SplitView sizes={[18, 82]} minSize={[180, 500]}>
        {/* Left Panel — Folder Tree */}
        <SplitView.Left>
          <Box pr="2">
            <Flex justify="between" align="center" mb="2" pb="2" style={{ borderBottom: "1px solid var(--gray-5)" }}>
              <Text size="2" weight="bold" color="gray" style={{ textTransform: "uppercase", letterSpacing: "0.5px" }}>Explorer</Text>
              <IconButton variant="ghost" size="1" onClick={() => setShowCreateFolder(true)} title="New folder">
                <FolderPlusIcon size={14} />
              </IconButton>
            </Flex>

            <Flex
              align="center" gap="2" py="1" px="2" mb="1"
              style={{ cursor: "pointer", backgroundColor: currentFolderId === null ? "var(--accent-3)" : "transparent", borderRadius: "var(--radius-2)" }}
              onClick={() => navigateToFolder(null)}
            >
              <HomeIcon size={14} />
              <Text size="2" weight={currentFolderId === null ? "bold" : "medium"}>All Files</Text>
            </Flex>

            <Box>
              {rootTreeFolders.map((folder, idx) => (
                <FolderTreeItem key={folder.id} folder={folder} isLast={idx === rootTreeFolders.length - 1} parentLines={[]} />
              ))}
            </Box>

            {rootTreeFolders.length === 0 && (
              <Text size="1" color="gray" style={{ display: "block", padding: "8px 12px" }}>No folders yet</Text>
            )}
          </Box>
        </SplitView.Left>

        {/* Right Panel — Content */}
        <SplitView.Right>
          <Box pl="2">
            {/* Toolbar */}
            <Flex justify="between" align="center" mb="3">
              <Flex align="center" gap="1" style={{ flexWrap: "wrap" }}>
                <Button variant={currentFolderId === null ? "solid" : "ghost"} size="1" onClick={() => navigateToFolder(null)}>
                  <HomeIcon size={12} /> Files
                </Button>
                {breadcrumb.map((item) => (
                  <Flex key={item.id} align="center" gap="1">
                    <ChevronRightIcon size={12} color="var(--gray-8)" />
                    <Button variant={item.id === currentFolderId ? "solid" : "ghost"} size="1" onClick={() => navigateToFolder(item.id)}>{item.name}</Button>
                  </Flex>
                ))}
              </Flex>
              <Flex gap="2" align="center">
                {selectedFiles.length > 0 && (
                  <BulkFileActions
                    selectedFiles={selectedFiles}
                    onBulkDownload={handleBulkDownload}
                    onBulkDelete={handleBulkDelete}
                    onBulkReprobe={handleBulkReprobe}
                  />
                )}
                <Button variant="outline" size="1" onClick={() => setShowCreateFolder(true)}>
                  <FolderPlusIcon size={12} /> New Folder
                </Button>
                <Button size="1" onClick={() => setShowUploadZone(!showUploadZone)}>
                  <UploadIcon size={12} /> Upload
                </Button>
              </Flex>
            </Flex>

            {/* Upload zone */}
            {showUploadZone && (
              <Box mb="3">
                <FileUploadZone onFileChange={handleFileUpload} accept="*/*" maxSize={50} multiple={true} loading={uploadingFiles} progress={uploadProgress} progressLabel={uploadLabel} height="5rem" />
              </Box>
            )}

            {/* Files and folders in unified grid */}
            <PaginatedFileGrid
              pageSize={25}
              enableSelection={true}
              multiple={true}
              selectedFiles={selectedFiles}
              onSelectionChanged={setSelectedFiles}
              showActions={true}
              onOpenGallery={handleOpenGallery}
              onDeleteFile={handleDeleteFile}
              folderId={currentFolderId ?? 0}
              folders={folders}
              onFolderClick={navigateToFolder}
              onFolderRename={(folder) => { setRenamingFolder(folder); setRenameValue(folder.name); }}
              onFolderDelete={handleDeleteFolder}
              onEditFile={setEditingFile}
              emptyState={
                <Flex direction="column" align="center" justify="center" gap="4" py="6">
                  <Box style={{ opacity: 0.5 }}>
                    <FolderIcon size={64} color="var(--accent-9)" fill="var(--accent-3)" strokeWidth={1} />
                  </Box>
                  <Flex direction="column" align="center" gap="1">
                    <Text size="3" weight="medium" color="gray">This folder is empty</Text>
                    <Text size="2" color="gray">Upload files or create a subfolder to get started</Text>
                  </Flex>
                  <FileUploadZone onFileChange={handleFileUpload} accept="*/*" maxSize={50} multiple={true} loading={uploadingFiles} progress={uploadProgress} progressLabel={uploadLabel} height="8rem" />
                </Flex>
              }
            />
          </Box>
        </SplitView.Right>
      </SplitView>

      {/* Gallery */}
      {galleryOpen && (
        <ImageGalleryOverlay
          files={galleryFiles}
          initialIndex={selectedImageIndex}
          onClose={() => setGalleryOpen(false)}
        />
      )}

      {/* Create folder dialog */}
      <Dialog.Root open={showCreateFolder} onOpenChange={setShowCreateFolder}>
        <Dialog.Content style={{ maxWidth: 400 }}>
          <Dialog.Title>New Folder</Dialog.Title>
          <TextField.Root value={newFolderName} onChange={(e) => setNewFolderName(e.target.value)} placeholder="Folder name" onKeyDown={(e) => e.key === "Enter" && handleCreateFolder()} autoFocus />
          <Flex gap="2" mt="4" justify="end">
            <Button variant="soft" onClick={() => setShowCreateFolder(false)}>Cancel</Button>
            <Button onClick={handleCreateFolder} disabled={!newFolderName.trim()}>Create</Button>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>

      {/* Rename dialog */}
      <Dialog.Root open={!!renamingFolder} onOpenChange={(open) => !open && setRenamingFolder(null)}>
        <Dialog.Content style={{ maxWidth: 400 }}>
          <Dialog.Title>Rename Folder</Dialog.Title>
          <TextField.Root value={renameValue} onChange={(e) => setRenameValue(e.target.value)} onKeyDown={(e) => e.key === "Enter" && handleRenameFolder()} autoFocus />
          <Flex gap="2" mt="4" justify="end">
            <Button variant="soft" onClick={() => setRenamingFolder(null)}>Cancel</Button>
            <Button onClick={handleRenameFolder} disabled={!renameValue.trim()}>Rename</Button>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>

      {/* File metadata dialog */}
      <Dialog.Root open={!!editingFile} onOpenChange={(open) => !open && setEditingFile(null)}>
        <Dialog.Content style={{ maxWidth: 500 }}>
          <Dialog.Title>File Details</Dialog.Title>
          {editingFile && (
            <FileMetadataForm
              file={editingFile}
              onSave={handleSaveFileMetadata}
              onCancel={() => setEditingFile(null)}
            />
          )}
        </Dialog.Content>
      </Dialog.Root>
    </PageTemplate>
  );
}

function FileMetadataForm({ file, onSave, onCancel }: {
  file: File;
  onSave: (data: Record<string, string | undefined>) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState(file.original_file_name);
  const [description, setDescription] = useState(file.description || "");
  const [altText, setAltText] = useState(file.alt_text || "");
  const [tags, setTags] = useState(file.tags || "");

  const category = resolveMediaCategory(file);
  const rows: { label: string; value: string }[] = [];
  rows.push({ label: "Size", value: formatFileSize(file.file_size) });
  rows.push({ label: "Type", value: file.file_type });
  const dims = formatDimensions(file.width, file.height);
  if (dims) rows.push({ label: category === "video" ? "Resolution" : "Dimensions", value: dims });
  const dur = formatDuration(file.duration_seconds);
  if (dur) rows.push({ label: "Duration", value: dur });
  const br = formatBitrate(file.bitrate);
  if (br) rows.push({ label: "Bitrate", value: br });
  if (category === "video" && file.video_frame_rate) rows.push({ label: "Frame rate", value: `${file.video_frame_rate.toFixed(2)} fps` });
  if (file.video_codec) rows.push({ label: "Video codec", value: file.video_codec });
  if (file.audio_codec) rows.push({ label: "Audio codec", value: file.audio_codec });
  if (file.audio_sample_rate) rows.push({ label: "Sample rate", value: `${(file.audio_sample_rate / 1000).toFixed(1)} kHz` });
  if (file.audio_channels) rows.push({ label: "Channels", value: file.audio_channels === 1 ? "Mono" : file.audio_channels === 2 ? "Stereo" : `${file.audio_channels} ch` });
  if (file.content_hash) rows.push({ label: "SHA-256", value: `${file.content_hash.slice(0, 12)}…` });

  return (
    <Flex direction="column" gap="3" mt="2">
      <Box style={{ borderTop: "1px solid var(--gray-a4)", borderBottom: "1px solid var(--gray-a4)", padding: "8px 0" }}>
        <Flex direction="column" gap="1">
          {rows.map((row) => (
            <Flex key={row.label} justify="between" gap="2">
              <Text size="1" color="gray">{row.label}</Text>
              <Text size="1" style={{ fontFamily: row.label === "SHA-256" ? "var(--code-font-family)" : undefined }}>{row.value}</Text>
            </Flex>
          ))}
        </Flex>
      </Box>
      <Box>
        <Text size="2" weight="medium" mb="1" style={{ display: "block" }}>File Name</Text>
        <TextField.Root value={name} onChange={(e) => setName(e.target.value)} />
      </Box>
      <Box>
        <Text size="2" weight="medium" mb="1" style={{ display: "block" }}>Description</Text>
        <TextField.Root value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Add a description..." />
      </Box>
      {file.file_type.startsWith("image/") && (
        <Box>
          <Text size="2" weight="medium" mb="1" style={{ display: "block" }}>Alt Text</Text>
          <TextField.Root value={altText} onChange={(e) => setAltText(e.target.value)} placeholder="Describe this image for accessibility..." />
        </Box>
      )}
      <Box>
        <Text size="2" weight="medium" mb="1" style={{ display: "block" }}>Tags</Text>
        <TextField.Root value={tags} onChange={(e) => setTags(e.target.value)} placeholder="comma, separated, tags" />
        <Text size="1" color="gray" mt="1" style={{ display: "block" }}>Separate with commas</Text>
      </Box>
      <Flex gap="2" mt="2" justify="end">
        <Button variant="soft" onClick={onCancel}>Cancel</Button>
        <Button onClick={() => onSave({
          original_file_name: name !== file.original_file_name ? name : undefined,
          description: description || undefined,
          alt_text: altText || undefined,
          tags: tags || undefined,
        })}>
          Save
        </Button>
      </Flex>
    </Flex>
  );
}
